import type React from 'react'
import {useCallback, useEffect, useMemo, useRef, useState, type Dispatch} from 'react'
import {graphql, useRelayEnvironment, useFragment, readInlineData} from 'react-relay'

import {VALUES} from '../constants/values'
import {LABELS} from '../constants/labels'
import {type ExtendedItemProps, ItemPicker} from './ItemPicker'
import {getIssueSearchQueries, type ItemGroup} from '../shared'
import {useItemPickerErrorFallback} from './useItemPickerErrorFallback'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import type {IssuePickerIssue$data, IssuePickerIssue$key} from './__generated__/IssuePickerIssue.graphql'
import {IssueClosedIcon, IssueOpenedIcon} from '@primer/octicons-react'
import type {IssuePickerQuery$key} from './__generated__/IssuePickerQuery.graphql'
import type {IssuePickerSearchQuery, IssuePickerSearchQuery$data} from './__generated__/IssuePickerSearchQuery.graphql'
import {useDebounce} from '@github-ui/use-debounce'
import {Octicon} from '@primer/react'

// A little alias to make referencing the data for this component's query, best kept unexported to prevent overzealous
// imports
type Issue = IssuePickerIssue$data

type IssuePickerBaseProps = {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  onIssueUpdate?: () => void
  onIssueSelection: (issues: Issue[]) => void
  repositoryNameWithOwner?: string
  title?: string | React.ReactElement
  triggerOpen?: boolean
  onClose?: () => void
}

type LazyIssuePickerProps = IssuePickerBaseProps & {
  owner?: string
  activeIssuesKeys: IssuePickerIssue$key[]
}

type IssuePickerInternalProps = IssuePickerBaseProps & {
  paginatedKey: IssuePickerQuery$key | null
  isLoading: boolean
  filter: string
  setFilter: Dispatch<string>
  activeIssues: Issue[]
}

const openGroup: ItemGroup = {groupId: 'open', header: {title: 'Open', variant: 'filled'}}
const closedGroup: ItemGroup = {groupId: 'closed', header: {title: 'Closed', variant: 'filled'}}

export const IssuePickerSearchGraphQLQuery = graphql`
  query IssuePickerSearchQuery(
    $commenters: String!
    $mentions: String!
    $assignee: String!
    $author: String!
    $open: String!
    $first: Int = 10
  ) {
    ...IssuePickerQuery
  }
`

export const IssuePickerQueryFragment = graphql`
  fragment IssuePickerQuery on Query {
    commenters: search(type: ISSUE, first: $first, query: $commenters) {
      nodes {
        ...IssuePickerIssue
      }
    }
    mentions: search(type: ISSUE, first: $first, query: $mentions) {
      nodes {
        ...IssuePickerIssue
      }
    }
    assignee: search(type: ISSUE, first: $first, query: $assignee) {
      nodes {
        ...IssuePickerIssue
      }
    }
    author: search(type: ISSUE, first: $first, query: $author) {
      nodes {
        ...IssuePickerIssue
      }
    }
    open: search(type: ISSUE, first: $first, query: $open) {
      nodes {
        ...IssuePickerIssue
      }
    }
  }
`

export const IssueFragment = graphql`
  fragment IssuePickerIssue on Issue @inline {
    id
    title
    closed
    state
  }
`

// Handles debouncing queries to fetch issues based on the current filter
const useIssueFiltering = (
  filter: string,
  owner: string = '',
  repositoryNameWithOwner?: string,
): {
  isLoading: boolean
  isError: boolean
  data: IssuePickerSearchQuery$data | null
} => {
  const environment = useRelayEnvironment()

  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<IssuePickerSearchQuery$data | null>(null)

  const fetchSearchData = useCallback(
    (nextFilter: string) =>
      clientSideRelayFetchQueryRetained<IssuePickerSearchQuery>({
        environment,
        query: IssuePickerSearchGraphQLQuery,
        variables: getIssueSearchQueries(owner, nextFilter, repositoryNameWithOwner),
      }).subscribe({
        next: (innerData: IssuePickerSearchQuery$data) => {
          setData(innerData ?? null)
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
          setIsLoading(false)
        },
      }),
    [environment, owner, repositoryNameWithOwner],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  useEffect(() => {
    debounceFetchSearchData(filter)
  }, [debounceFetchSearchData, filter])

  return {isLoading, isError, data}
}

export function LazyIssuePicker({
  onIssueSelection,
  activeIssuesKeys,
  owner,
  repositoryNameWithOwner,
  ...rest
}: LazyIssuePickerProps) {
  const activeIssues = useMemo(
    () =>
      activeIssuesKeys.flatMap(key => {
        if (key === undefined) return []
        // Given how the ItemPicker interacts with the items that it has loaded, we must read the relay data inline
        // here to pass to the ItemPicker, as opposed to reading the fragment for each node in another element.
        // eslint-disable-next-line no-restricted-syntax
        return readInlineData<IssuePickerIssue$key>(IssueFragment, key) || []
      }),
    [activeIssuesKeys],
  )

  const [filter, setFilter] = useState('')
  const {isLoading, isError, data} = useIssueFiltering(filter, owner, repositoryNameWithOwner)

  // Render a fallback if there is an error fetching the data from the filter
  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('issues'),
    anchorElement: rest.anchorElement,
    open: true,
  })
  if (isError) {
    return createFallbackComponent(() => {})
  }

  return (
    <IssuePickerInternal
      paginatedKey={data}
      isLoading={isLoading}
      {...rest}
      activeIssues={activeIssues}
      filter={filter}
      setFilter={setFilter}
      onIssueSelection={onIssueSelection}
      repositoryNameWithOwner={repositoryNameWithOwner}
    />
  )
}

function IssuePickerInternal({
  paginatedKey,
  activeIssues,
  onIssueSelection,
  anchorElement,
  setFilter,
  isLoading,
  title,
  onClose,
  triggerOpen,
}: IssuePickerInternalProps) {
  const data = useFragment(IssuePickerQueryFragment, paginatedKey)
  const fetchedIssues = useMemo(() => {
    // Order the unique issues from each of the queries. Changing the order of this spread
    // will change the relative priority of each individual search query.
    const allIssues = [
      ...(data?.commenters.nodes || []),
      ...(data?.mentions.nodes || []),
      ...(data?.assignee.nodes || []),
      ...(data?.author.nodes || []),
      ...(data?.open.nodes || []),
    ]
    const issuesMap = new Map<string, IssuePickerIssue$data>()
    for (const issue of allIssues) {
      if (issue === undefined) continue
      // Given how the ItemPicker interacts with the items that it has loaded, we must read the relay data inline
      // here to pass to the ItemPicker, as opposed to reading the fragment for each node in another element.
      // eslint-disable-next-line no-restricted-syntax
      const readData = readInlineData<IssuePickerIssue$key>(IssueFragment, issue)
      if (readData) {
        issuesMap.set(readData.id, readData)
      }
    }
    return Array.from(issuesMap.values())
  }, [data])

  const getItemKey = useCallback((issue: Issue) => issue.id, [])

  const convertToItemProps = useCallback((issue: Issue): ExtendedItemProps<Issue> => {
    return {
      id: issue.id,
      text: issue.title,
      sx: {wordBreak: 'break-word'},
      source: issue,
      groupId: issue.closed ? closedGroup.groupId : openGroup.groupId,
      leadingVisual: () => (
        <Octicon
          icon={issue.state === 'CLOSED' ? IssueClosedIcon : IssueOpenedIcon}
          sx={{path: {fill: issue.state === 'CLOSED' ? 'done.fg' : 'open.fg'}}}
        />
      ),
    }
  }, [])

  const issuePickerRef = useRef<HTMLButtonElement>(null)

  return (
    <ItemPicker
      loading={isLoading}
      items={fetchedIssues}
      initialSelectedItems={activeIssues}
      filterItems={setFilter}
      title={title || LABELS.issueHeader}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText="Search issues"
      selectionVariant="single"
      onSelectionChange={onIssueSelection}
      renderAnchor={anchorElement}
      height="large"
      width="medium"
      resultListAriaLabel="issue results"
      selectPanelRef={issuePickerRef}
      triggerOpen={triggerOpen}
      onClose={onClose}
    />
  )
}

try{ LazyIssuePicker.displayName ||= 'LazyIssuePicker' } catch {}
try{ IssuePickerInternal.displayName ||= 'IssuePickerInternal' } catch {}